import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { Badge } from '../ui/Badge';
import { PROVIDER_MODELS, type AiProvider } from '../../lib/aiAgents';

interface ProviderCardProps {
  provider: AiProvider;
  configured: boolean;
  keyMasked: string | null;
  keyValue: string;
  onChangeKey: (val: string) => void;
}

const PROVIDER_METAS: Record<
  AiProvider,
  { name: string; iconBg: string; placeholder: string; docUrl?: string }
> = {
  anthropic: {
    name: 'Anthropic (Claude)',
    iconBg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
    placeholder: 'sk-ant-api03-...',
    docUrl: 'https://console.anthropic.com',
  },
  openai: {
    name: 'OpenAI (GPT & Reasoning)',
    iconBg: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
    placeholder: 'sk-proj-...',
    docUrl: 'https://platform.openai.com',
  },
  deepseek: {
    name: 'DeepSeek (Chat & Reasoner)',
    iconBg: 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200',
    placeholder: 'sk-...',
    docUrl: 'https://platform.deepseek.com',
  },
  gemini: {
    name: 'Google Gemini',
    iconBg: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
    placeholder: 'AIzaSy...',
    docUrl: 'https://aistudio.google.com',
  },
};

export function ProviderCard({
  provider,
  configured,
  keyMasked,
  keyValue,
  onChangeKey,
}: ProviderCardProps) {
  const { t } = useI18n();
  const meta = PROVIDER_METAS[provider];
  const models = PROVIDER_MODELS[provider] || [];
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="flex flex-col justify-between rounded-xl border border-line bg-surface p-5 shadow-sm transition hover:shadow dark:border-slate-700 dark:bg-slate-800">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl font-bold text-sm ${meta.iconBg}`}
            >
              {provider.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h4 className="text-sm font-bold text-ink dark:text-white">{meta.name}</h4>
              <span className="text-[11px] text-ink-3 dark:text-slate-400">
                {models.length} {t('ai.providers.supportedModels')}
              </span>
            </div>
          </div>
          <Badge variant={configured ? 'success' : 'slate'}>
            {configured ? t('ai.providers.configured') : t('ai.providers.notConfigured')}
          </Badge>
        </div>

        {provider === 'gemini' && (
          <div className="mt-3 rounded-lg bg-indigo-50/70 p-2.5 text-[11px] text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300">
            {t('ai.providers.geminiNote')}
          </div>
        )}

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.apiKey')}
            </label>
            {configured && (
              <span className="text-[11px] text-brand dark:text-brand-tint">
                {keyMasked || '••••••••'} ({t('ai.keySet')})
              </span>
            )}
          </div>

          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              value={keyValue}
              onChange={(e) => onChangeKey(e.target.value)}
              placeholder={
                configured ? t('ai.keyReplace') : meta.placeholder
              }
              className="w-full rounded-lg border border-line bg-surface py-2 pe-10 ps-3 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 end-0 flex items-center pe-3 text-ink-3 hover:text-ink dark:text-slate-400 dark:hover:text-white"
              title="Toggle visibility"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {showPassword ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                ) : (
                  <>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-4">
          <span className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400">
            {t('ai.providers.models')}:
          </span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {models.map((m) => (
              <span
                key={m}
                className="rounded-md border border-line bg-surface-subtle px-1.5 py-0.5 text-[10px] font-medium text-ink-2 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
